import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InterestClusterBarChart from './InterestClusterBarChart';
import InterestClusterTileGrid from './InterestClusterTileGrid';
import InterestClusterPlaceholder from './InterestClusterPlaceholder';
import AlgorithmResultsDisclaimer from '../test/AlgorithmResultsDisclaimer';
import { fetchInterestClusterScores } from '../../utils/interestScoringApi';
import { CURRENT_INTEREST_BANK_VERSION, isScoreableInterestVectorLength } from '../../config/productVectors';

function interestScoreErrorMessage(err, t) {
  if (err?.code === 'INTEREST_BANK_STALE' || err?.status === 409) {
    return t('report.interestClustersStale');
  }
  if (err?.status === 404) {
    return t('report.interestClustersNoResult');
  }
  return t('report.interestClustersLoadFailed');
}

function isStaleInterestState(interests, interestBankVersion) {
  const vectorLen = Array.isArray(interests) ? interests.length : 0;
  return vectorLen > 0 && interestBankVersion !== CURRENT_INTEREST_BANK_VERSION;
}

/**
 * Interest cluster recommendation widget — visible to any logged-in user with a completed test.
 * @param {{ interests?: Array<{interest_id, choice}>, interestBankVersion?: string | null, userId?: string, showTakeTestLink?: boolean, hasCompletedTest?: boolean, variant?: 'tiles'|'bars', embedded?: boolean, revealSequence?: boolean, fullWidthRows?: boolean }} props
 */
export default function InterestClusterWidget({
  interests,
  interestBankVersion = null,
  userId,
  showTakeTestLink = true,
  hasCompletedTest = false,
  variant = 'tiles',
  embedded = false,
  revealSequence = false,
  fullWidthRows = false
}) {
  const { t } = useTranslation();
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEmpty, setShowEmpty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const vectorLen = Array.isArray(interests) ? interests.length : 0;
    const hasCurrentBank =
      interestBankVersion === CURRENT_INTEREST_BANK_VERSION &&
      isScoreableInterestVectorLength(vectorLen);
    const isStale =
      vectorLen > 0 && interestBankVersion !== CURRENT_INTEREST_BANK_VERSION;
    const isMissing = vectorLen === 0;

    if (isMissing) {
      setLoading(false);
      setClusters([]);
      setError('');
      setShowEmpty(true);
      return undefined;
    }

    if (isStale) {
      setLoading(false);
      setClusters([]);
      setError(t('report.interestClustersStale'));
      setShowEmpty(false);
      return undefined;
    }

    (async () => {
      setLoading(true);
      setError('');
      setShowEmpty(false);
      try {
        const scores = await fetchInterestClusterScores({
          ...(hasCurrentBank ? { interests } : {}),
          ...(userId ? { userId } : {})
        });
        if (!cancelled) setClusters(scores);
      } catch (e) {
        if (!cancelled) {
          setClusters([]);
          setError(interestScoreErrorMessage(e, t));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [interests, interestBankVersion, userId, t]);

  const noData = !loading && !error && !showEmpty && clusters.length === 0;
  const showRetakeLink = hasCompletedTest || clusters.length > 0;
  const showPlaceholderTeaser = showEmpty || (Boolean(error) && isStaleInterestState(interests, interestBankVersion));

  const shellClass = embedded
    ? ''
    : 'bg-surface rounded-lg shadow-sm border border-edge p-6 mb-6';

  return (
    <div className={shellClass}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h3 className="text-xl font-bold text-ink">{t('report.interestClustersHeading')}</h3>
        {showTakeTestLink && !showPlaceholderTeaser ? (
          <Link
            to="/interest-test"
            className="inline-flex items-center justify-center rounded-full border border-link/30 bg-accent/25 px-3 py-1.5 text-sm font-medium text-link hover:bg-accent/40 transition-colors"
          >
            {showRetakeLink
              ? t('interestTest.retakeTest')
              : t('report.interestClustersTakeTest')}
          </Link>
        ) : null}
      </div>
      <p className="text-sm text-ink-muted mb-2">
        {showPlaceholderTeaser
          ? t('interestTest.statsPlaceholderDescription')
          : t('report.interestClustersDescription')}
      </p>

      {loading ? (
        <p className="text-sm text-ink-subtle">{t('report.interestClustersLoading')}</p>
      ) : showPlaceholderTeaser ? (
        <>
          {error ? <p className="text-sm text-amber-700 mb-4">{error}</p> : null}
          <InterestClusterPlaceholder omitHeading />
        </>
      ) : error ? (
        <p className="text-sm text-amber-700">{error}</p>
      ) : noData ? (
        <p className="text-sm text-ink-subtle">{t('report.interestClustersEmpty')}</p>
      ) : variant === 'bars' ? (
        <>
          <InterestClusterBarChart clusters={clusters} />
          <AlgorithmResultsDisclaimer variant="interest" className="mt-4" />
        </>
      ) : (
        <>
          <InterestClusterTileGrid
            clusters={clusters}
            revealSequence={revealSequence}
            fullWidthRows={fullWidthRows}
          />
          <AlgorithmResultsDisclaimer variant="interest" className="mt-4" />
        </>
      )}
    </div>
  );
}
